import { useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { useAuthContext } from "providers/Auth";
import { Dispatch, SetStateAction, useState } from "react";
import { Link } from "react-router-dom";
import { AdjustedApplicationRentActivity } from "shared/app/ActivityLog/types";
import { Card } from "shared/components/Card";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { CriteriaRentType } from "shared/types";
import { ApplicationStatus, Money } from "shared/types/application";
import { formatToCurrency } from "shared/utils";
import { Property, Unit } from "types/landlord/address";
import { Stat } from "./components/Stat";
import { HugeiconsIcon } from "@hugeicons/react";
import { Home02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { ChangeDesiredMoveInDateModal } from "./components/ChangeDesiredMoveInDateModal";
import {
  Show,
  Button,
  ButtonSize,
  ButtonVariant,
  Badge,
  BadgeSize,
  BadgeVariant
} from "shared/components";
import { ChangeDesiredLeaseTermModal } from "./components/ChangeDesiredLeaseTermModal";
import { EditApplicationRentModal } from "views/applications-id/components/LayoutHeader/components/ApplicationRentSection/EditApplicationRentModal";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { IconButton, IconButtonVariant } from "shared/components/IconButton";
import { DotsVertical } from "untitledui-js-base";
import { CopyIconButton } from "shared/components/CopyIconButton";
import { cx } from "shared/utils";
import { useFeatureFlagEnabled } from "posthog-js/react";

const formatPropertyAddress = (address: {
  address1: string;
  city: string;
  state: string;
  state_or_province: string;
  zip: string;
  postal_code: string;
}): string => {
  const state = address.state || address.state_or_province;
  const zip = address.zip || address.postal_code;
  const parts = [
    address.address1,
    address.city,
    [state, zip].filter(Boolean).join(" ")
  ].filter(Boolean);
  return parts.join(", ");
};

const statsGridClassName = "grid w-full grid-cols-2 lg:grid-cols-4";
const statCellClassName =
  "border-l border-t border-alpha-40 first:border-l-0 px-6 py-4 h-24";
const lastRowStatCellClassName = `${statCellClassName} border-b`;

export const ApplicationOverview = ({
  applicationId,
  applicationRentAmount,
  setShowActivityLog,
  applicationStatus,
  latestApplicationRentHistory,
  unit,
  property,
  desiredMoveInDate,
  desiredLeaseTerm,
  criteriaRentType,
  refetchApplication,
  onViewTemplate,
  templateName
}: {
  applicationId: string;
  applicationRentAmount: {
    monthly_rent_amount?: Money;
    rent_amount?: Money;
    home_rent_amount?: Money;
    site_rent_amount?: Money;
    previous_rent_amount?: Money;
    housing_assistance_voucher_amount?: Money;
    pet_rent_amount?: Money;
  };
  setShowActivityLog: Dispatch<SetStateAction<boolean>>;
  applicationStatus: ApplicationStatus;
  latestApplicationRentHistory?: AdjustedApplicationRentActivity;
  unit: Unit;
  property: Property;
  desiredMoveInDate?: string;
  desiredLeaseTerm?: number;
  criteriaRentType: CriteriaRentType;
  refetchApplication: () => void;
  onViewTemplate?: () => void;
  templateName?: string;
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const { permissions, pms, type } = useAuthContext();
  const showUnitType = useFeatureFlagEnabled("show-unit-type");
  const homeSiteRentEnabled = pms.isManageAmerica || type.isHomeSiteRentEnabled;
  const queryClient = useQueryClient();

  const [openedDesiredMoveInDateModal, setOpenedDesiredMoveInDateModal] =
    useState(false);
  const [openedDesiredLeaseTermModal, setOpenedDesiredLeaseTermModal] =
    useState(false);
  const [showEditRentModal, setShowEditRentModal] = useState(false);

  const propertyAddress = property.address.address1
    ? formatPropertyAddress(property.address)
    : "";

  const canEditApplicationRent =
    permissions.canEditApplicationRent &&
    [
      ApplicationStatus.Pending,
      ApplicationStatus.Started,
      ApplicationStatus.PartiallySubmitted,
      ApplicationStatus.Submitted
    ].includes(applicationStatus);

  const unitTitleContent = (
    <>
      <Link
        to={`/properties/${property.id}/units/${unit.id}`}
        className="truncate text-sm font-bold text-primary-500 hover:underline"
      >
        {unit.title || "--"}
      </Link>
      <Show if={Boolean(unit.title)}>
        <CopyIconButton
          value={unit.title || ""}
          idleAriaLabel="Copy unit title"
        />
      </Show>
    </>
  );

  const desiredLeaseTermMonths = desiredLeaseTerm === 1 ? "month" : "months";
  const desiredLeaseTermLabel = desiredLeaseTerm
    ? `${desiredLeaseTerm} ${desiredLeaseTermMonths}`
    : "None";

  return (
    <Card className="w-full">
      <div className="flex items-center gap-4 px-6 py-4">
        <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-primary-20">
          <HugeiconsIcon
            icon={Home02Icon}
            size={24}
            className="text-primary-500"
          />
        </div>
        <div className="min-w-0 flex-1 space-y-1">
          <Link
            to={`/properties/${property.id}`}
            className="block truncate text-base font-bold text-primary-500 hover:underline"
          >
            {property.name || "--"}
          </Link>
          <Show if={Boolean(propertyAddress)}>
            <div className="flex items-center gap-2">
              <p className="truncate text-sm font-medium text-content-400">
                {propertyAddress}
              </p>
              <CopyIconButton
                value={propertyAddress}
                idleAriaLabel="Copy address"
              />
            </div>
          </Show>
        </div>
      </div>

      <div className={statsGridClassName}>
        <Stat
          className={statCellClassName}
          label={`${dictionary.Property} group`}
          value={property.property_group?.name || "--"}
        />
        {showUnitType ? (
          <div
            className={cx(
              statCellClassName,
              "flex min-w-0 flex-col justify-center"
            )}
          >
            <div className="mb-2 flex min-h-6 min-w-0 items-center gap-2">
              <span className="shrink-0 text-sm font-normal text-content-400">
                {dictionary.Unit}
              </span>
              {unitTitleContent}
            </div>
            <div className="flex min-w-0 items-center gap-2">
              <span className="shrink-0 text-sm font-normal text-content-400">
                Unit type
              </span>
              <p className="truncate text-sm font-medium text-content-1000">
                {unit.unit_type || "--"}
              </p>
            </div>
          </div>
        ) : (
          <Stat
            className={statCellClassName}
            label={`${dictionary.Unit}`}
            value={
              <div className="flex items-center gap-2">{unitTitleContent}</div>
            }
          />
        )}
        <Stat
          className={statCellClassName}
          label="Available"
          value={
            unit.available_at && moment(unit.available_at).isValid()
              ? moment(unit.available_at).utc().format("MMM D, YYYY")
              : "--"
          }
        />
        <Stat
          className={statCellClassName}
          label="Desired lease term"
          value={
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-content-1000 truncate">
                {desiredLeaseTermLabel}
              </p>
              <Button
                variant={ButtonVariant.TEXT}
                size={ButtonSize.BASE}
                onClick={() => setOpenedDesiredLeaseTermModal(true)}
                className="shrink-0 !text-[13px]"
              >
                Edit
              </Button>
            </div>
          }
        />
      </div>

      <div className={statsGridClassName}>
        <Stat
          className={lastRowStatCellClassName}
          label="Desired move-in"
          value={
            <div className="flex items-center gap-2">
              <p className="text-sm font-medium text-content-1000 truncate">
                {desiredMoveInDate && moment(desiredMoveInDate).isValid()
                  ? moment(desiredMoveInDate).format("MMM D, YYYY")
                  : "--"}
              </p>
              <Show if={permissions.canChangeDesiredMoveInDate}>
                <Button
                  variant={ButtonVariant.TEXT}
                  size={ButtonSize.BASE}
                  onClick={() => setOpenedDesiredMoveInDateModal(true)}
                  className="shrink-0 !text-[13px]"
                >
                  Edit
                </Button>
              </Show>
            </div>
          }
        />
        {!homeSiteRentEnabled ? (
          <Stat
            className={lastRowStatCellClassName}
            label="Listed rent"
            value={formatToCurrency({
              cents: unit.monthly_rent_amount?.cents || 0,
              currency: "USD"
            })}
          />
        ) : (
          <div
            className={cx(
              lastRowStatCellClassName,
              "flex flex-col justify-center gap-2"
            )}
          >
            <div className="flex items-center gap-2">
              <span className="w-28 shrink-0 text-sm font-normal text-content-400">
                Listed home rent
              </span>
              <p className="text-sm font-medium text-content-1000">
                {formatToCurrency({
                  cents: unit.home_rent_amount?.cents || 0,
                  currency: "USD"
                })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-28 shrink-0 text-sm font-normal text-content-400">
                Listed site rent
              </span>
              <p className="text-sm font-medium text-content-1000">
                {formatToCurrency({
                  cents: unit.site_rent_amount?.cents || 0,
                  currency: "USD"
                })}
              </p>
            </div>
          </div>
        )}
        <Stat
          className={lastRowStatCellClassName}
          label={
            <span className="flex w-full items-center justify-between">
              <span className="flex flex-row gap-1.5 items-center">
                <p>Application rent</p>
                <Show if={Boolean(latestApplicationRentHistory)}>
                  <Badge variant={BadgeVariant.BASE} size={BadgeSize.SM}>
                    Edited
                  </Badge>
                </Show>
              </span>

              <ContextMenuPopover
                cbTrigger={() => (
                  <IconButton
                    variant={IconButtonVariant.TEXT_BASE}
                    className="!stroke-content-1000 !p-0 size-5 align-middle [&>svg]:block"
                    Icon={DotsVertical}
                  />
                )}
                actions={[
                  ...(latestApplicationRentHistory
                    ? [
                        {
                          title: "View edit details",
                          onClick: () => setShowActivityLog(true)
                        }
                      ]
                    : []),
                  ...(canEditApplicationRent
                    ? [
                        {
                          title: "View and edit rent",
                          onClick: () => setShowEditRentModal(true)
                        }
                      ]
                    : [])
                ]}
              />
            </span>
          }
          value={
            <p className="text-sm font-medium text-content-1000 truncate">
              {formatToCurrency(
                applicationRentAmount.monthly_rent_amount || null
              )}
            </p>
          }
        />
        <Show if={Boolean(templateName)}>
          <Stat
            className={lastRowStatCellClassName}
            label="Screening template"
            value={
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium text-content-1000 truncate">
                  {templateName}
                </p>
                <Show
                  if={
                    Boolean(onViewTemplate) &&
                    applicationStatus === ApplicationStatus.Submitted
                  }
                >
                  <Button
                    variant={ButtonVariant.TEXT}
                    size={ButtonSize.BASE}
                    onClick={() => onViewTemplate?.()}
                    className="shrink-0 !text-[13px]"
                  >
                    Edit
                  </Button>
                </Show>
              </div>
            }
          />
        </Show>
      </div>
      <Show if={permissions.canChangeDesiredMoveInDate}>
        <ChangeDesiredMoveInDateModal
          opened={openedDesiredMoveInDateModal}
          onClose={() => setOpenedDesiredMoveInDateModal(false)}
          applicationId={applicationId}
          desiredMoveInDate={desiredMoveInDate || ""}
          refetchApplication={refetchApplication}
        />
      </Show>
      <ChangeDesiredLeaseTermModal
        opened={openedDesiredLeaseTermModal}
        onClose={() => setOpenedDesiredLeaseTermModal(false)}
        applicationId={applicationId}
        desiredLeaseTerm={desiredLeaseTerm || 0}
        refetchApplication={refetchApplication}
      />
      <EditApplicationRentModal
        opened={showEditRentModal}
        onClose={() => setShowEditRentModal(false)}
        applicationId={applicationId}
        applicationRentAmount={applicationRentAmount}
        onSuccess={() => {
          queryClient.invalidateQueries({
            queryKey: ["APPLICATION", applicationId]
          });
        }}
        criteriaRentType={criteriaRentType}
      />
    </Card>
  );
};
